import axios from 'axios';
import type { CotizaTodasResponse } from '@/types/quote.types';
import { getAgentId, getInsurerCotizaUrl } from '@/config/api';
import { getWidgetAgentData } from '@/utils/widgetAgent';
import { translateInsuranceError } from '@/utils/errorMessages';

export type SupportedCompany = 'QUALITAS' | 'ANA';

export interface CotizaTodasOptions {
  idCotizacion: number;
  formaPago: string;
  paquete: string;
  company: SupportedCompany;
  postalCode?: string;
}

interface ServerErrorBody {
  Message?: string;
  ExceptionMessage?: string;
  errorMessage?: string;
  successMessage?: string;
}

const getCompanyName = (company: SupportedCompany): string =>
  company === 'ANA' ? 'ANA Seguros' : 'Qualitas';

const buildFormBody = (options: CotizaTodasOptions): string => {
  const { idCotizacion, formaPago, paquete, postalCode } = options;
  const formData = new URLSearchParams();
  formData.append('Paquete', paquete);
  formData.append('Forma_Pago', formaPago);
  formData.append('Id_Cotizacion', String(idCotizacion));
  formData.append('Id_Aut_Colectividad', '1');
  formData.append('UsuarioAdd', String(getWidgetAgentData().Id_Usuario));
  formData.append('Codigo_Postal', String(postalCode || ''));

  const agentId = getAgentId();
  if (agentId) {
    // AgentId goes in the body instead of a header to avoid CORS problems
    formData.append('AgentId', agentId);
  }
  return formData.toString();
};

const translateCaughtError = (error: unknown, companyName: string): Error => {
  if (axios.isAxiosError(error)) {
    const errorData = error.response?.data as ServerErrorBody | undefined;
    if (errorData) {
      const detailed =
        errorData.ExceptionMessage ||
        errorData.Message ||
        errorData.errorMessage ||
        errorData.successMessage ||
        error.message;
      return new Error(translateInsuranceError(detailed, companyName));
    }
    const fallback = `Error al obtener cotización de ${companyName}`;
    return new Error(translateInsuranceError(error.message || fallback, companyName));
  }

  if (error instanceof Error) {
    return new Error(translateInsuranceError(error.message, companyName));
  }

  return new Error(`Error desconocido al obtener cotización de ${companyName}`);
};

const postCotiza = async (
  url: string,
  body: string,
  companyName: string
): Promise<CotizaTodasResponse> => {
  const response = await axios.post<CotizaTodasResponse>(url, body, {
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
  });

  if (response.data.success) {
    return response.data;
  }

  const defaultErrorMsg = `Error al obtener cotización de ${companyName}`;
  const rawErrorMsg =
    response.data.errorMessage || response.data.successMessage || defaultErrorMsg;
  console.error('Error en respuesta de cotizaTodas:', response.data);
  throw new Error(translateInsuranceError(rawErrorMsg, companyName));
};

export const requestCotizaTodas = async (
  options: CotizaTodasOptions
): Promise<CotizaTodasResponse> => {
  if (!options.idCotizacion || options.idCotizacion <= 0) {
    throw new Error('Id_Cotizacion inválido o no proporcionado');
  }

  const url = getInsurerCotizaUrl(options.company);
  const body = buildFormBody(options);
  const companyName = getCompanyName(options.company);
  try {
    return await postCotiza(url, body, companyName);
  } catch (error) {
    console.error('cotizaTodas - Error completo:', error);
    throw translateCaughtError(error, companyName);
  }
};
